import React from 'react'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { TickMinor } from '@shopify/polaris-icons'
import type { GigsafeUser } from 'src/interfaces/gigsafeUser'
import { formatDateTime, parseFlexibleDateTime } from 'src/utils/grid'
import { gigsafeUsersTableHeaderTitles } from './constants'
import { GigsafeUsersTableFields } from './enums'

/**
 * Columns match the Retool tab exactly: ID, Name, Email, Disabled, Disabled at. Read-only —
 * enable/disable is out of scope for ENG-480 (design doc §2.1), so there is no actions column
 * and no handler to close over. Still a factory, like the other tables' column builders — it just
 * takes no arguments, because there is nothing here to parameterize it with.
 */
export const buildGigsafeUserColumns = (): Array<GridColDef<GigsafeUser>> => {
  return [
    {
      field: GigsafeUsersTableFields.id,
      headerName: gigsafeUsersTableHeaderTitles[GigsafeUsersTableFields.id],
      flex: 2,
      minWidth: 260,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<GigsafeUser>) => {
        return (
          <Typography variant="body2" css={tw`truncate text-shades-500`}>
            {params.row.id}
          </Typography>
        )
      },
    },
    {
      field: GigsafeUsersTableFields.name,
      headerName: gigsafeUsersTableHeaderTitles[GigsafeUsersTableFields.name],
      flex: 2,
      minWidth: 160,
      // The backend only sorts on firstName/lastName and this table does not send `sort`
      // (design doc §2.1), so client sort on a server-paginated page would reorder one page in
      // isolation and read as broken.
      sortable: false,
      // Name identifies the row, so it is never offered for hiding in the manage-columns panel.
      hideable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<GigsafeUser>) => {
        const name = `${params.row.firstName ?? ''} ${params.row.lastName ?? ''}`.trim()

        if (!name) {
          return (
            <Typography variant="body2" css={tw`text-shades-500`}>
              —
            </Typography>
          )
        }

        return (
          <Typography variant="body2" css={tw`truncate`}>
            {name}
          </Typography>
        )
      },
    },
    {
      field: GigsafeUsersTableFields.email,
      headerName: gigsafeUsersTableHeaderTitles[GigsafeUsersTableFields.email],
      flex: 2,
      minWidth: 200,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<GigsafeUser>) => {
        return (
          <Typography variant="body2" css={tw`truncate`}>
            {params.row.email}
          </Typography>
        )
      },
    },
    {
      field: GigsafeUsersTableFields.disabled,
      headerName: gigsafeUsersTableHeaderTitles[GigsafeUsersTableFields.disabled],
      flex: 1,
      minWidth: 110,
      sortable: false,
      display: 'flex',
      // A tick when disabled and an EMPTY cell otherwise, matching Retool — which renders
      // nothing rather than a "No" for an active user.
      renderCell: (params: GridRenderCellParams<GigsafeUser>) => {
        if (!params.row.disabled) {
          return null
        }

        return <TickMinor width={20} css={tw`text-shades-1000`} aria-label="Disabled" />
      },
    },
    {
      field: GigsafeUsersTableFields.disabledAt,
      headerName: gigsafeUsersTableHeaderTitles[GigsafeUsersTableFields.disabledAt],
      flex: 2,
      minWidth: 180,
      sortable: false,
      // `disabledAt` reaches the wire through getRawMany() so its exact shape is unverified —
      // parseFlexibleDateTime accepts ISO, Date and unix, and yields null for anything else so
      // the cell degrades to an em dash rather than "Invalid Date".
      valueGetter: parseFlexibleDateTime,
      valueFormatter: formatDateTime,
    },
  ]
}
